import { useEffect } from 'react';
import { useTracking } from 'react-tracking';
import Media from 'react-media';

import loadable from '@loadable/component';
import { useTheme } from '@simplywallst/ui-core';
import NotFound from '@/pages/NotFound';
import { useNarrative } from '@/pages/Narrative/hooks/useNarrative';
import { Footer } from '@components/Footer';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { Sentry } from '@/components/Sentry';
import useUser from '@/hooks/useUser';

import { useNarrativeSlug } from '../../hooks';
import { PageLayout } from './components/styled';
import PageSkeleton from './components/PageSkeleton';
import { NarrativeSchema } from './components/NarrativeSchema';
import { setLastVisitedStockForMiniValuator } from '../../utils/storage';

const DesktopLayout = loadable(() => import('./components/DesktopLayout'), {
  resolveComponent: (component) => component.DesktopLayout,
});
const MobileLayout = loadable(() => import('./components/MobileLayout'), {
  resolveComponent: (component) => component.MobileLayout,
});

export const Narrative = () => {
  const theme = useTheme();
  const { isDesktop } = useUserAgent();
  const { slug } = useNarrativeSlug();
  const { data: narrativeResponse, isLoading, error } = useNarrative({ slug });
  const narrative = narrativeResponse?.Narrative;
  const { data: user } = useUser();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'narrative',
    companyId: narrative?.companyId,
    uniqueSymbol: narrative?.company?.uniqueSymbol,
    narrativeId: narrative?.id,
    userId: user?.id,
    author: {
      id: narrative?.author.id,
      name: narrative?.author.name,
      role: narrative?.author.role,
    },
  });

  useEffect(() => {
    if (narrative?.id) {
      if (slug && !window.location.hash) {
        window.scrollTo(0, 0);
      }
      trackEvent({
        action: 'view',
        announcementCount: narrative.announcements?.length,
      });
      if (narrative.company?.canonicalURL) {
        setLastVisitedStockForMiniValuator(narrative?.company?.canonicalURL);
      }
    }
  }, [narrative, slug, trackEvent]);

  if (isLoading && RUNTIME_ENV !== 'server') return <PageSkeleton />;

  if (error || typeof narrative === 'undefined') return <NotFound />;

  return (
    <Track>
      <PageLayout>
        <Sentry
          sentryCapture={{ team: 'narwhals', extra: { slug } }}
          globalDisabled
        >
          {RUNTIME_ENV === 'server' && (
            <NarrativeSchema narrative={narrative} />
          )}
          <Media
            query={{ minWidth: theme.lgMinRaw }}
            defaultMatches={isDesktop}
          >
            {(isMatchDesktop) =>
              isMatchDesktop ? (
                <DesktopLayout narrative={narrative} />
              ) : (
                <MobileLayout narrative={narrative} />
              )
            }
          </Media>
          <Footer />
        </Sentry>
      </PageLayout>
    </Track>
  );
};
